import React, { useMemo } from 'react';
import type { FrequencyEnum, ProxyTypeEnum } from 'venn-api';
import type { FirstLayerProxyTypeValue, SecondLayerProxyTypeValue } from 'venn-utils';
import type { FundToBulkProxy, FundToProxy } from '../../types';
import type { ReturnsRangeInfo } from '../utils';
import { typeSupportsDesmooth, calculateBackfilledRange } from '../utils';
import type { LayeredProxyConfiguration } from '../proxy-types';
import { LayeredProxyConfiguration as LayeredProxyConfigurationEnum } from '../proxy-types';
import type { BulkPickerDetailedProxyMetadata } from '../../../../hooks/useProxyDataQuery';
import { FirstLayerProxyTypeDropdown } from './FirstLayerProxyTypeDropdown';
import { SecondLayerProxyTypeSelector } from './SecondLayerProxyTypeSelector';
import { DesmoothingLag } from '../DesmoothingLag';
import { DesmoothingLagHelpText } from '../DesmoothingLagHelpText';
import { cn } from '@/lib/utils';

interface LayeredProxyTypeSelectorProps {
  firstLayerProxyTypeValue: FirstLayerProxyTypeValue | undefined;
  onFirstLayerProxyTypeSelect: (value: FirstLayerProxyTypeValue) => void;
  investments: (FundToProxy | FundToBulkProxy)[];
  rawInvestmentRanges: (ReturnsRangeInfo | null)[];
  firstLayerProxyRange: ReturnsRangeInfo | undefined;
  secondLayerProxyRange: ReturnsRangeInfo | undefined;
  withExtrapolation: boolean;
  showSecondLayerControls: boolean;
  secondLayerProxyTypeValue: SecondLayerProxyTypeValue | undefined;
  onSecondLayerProxyTypeSelect: (value: SecondLayerProxyTypeValue) => void;
  proxyConfiguration: LayeredProxyConfiguration;
  onRemoveSecondLayer: () => void;
  secondLayerNumLags: number;
  secondLayerSuggestedNumLags: number | undefined;
  onSecondLayerLagUpdate: (lags: number) => void;
  secondLayerFrequency: FrequencyEnum | undefined;
  selectedProxyType: ProxyTypeEnum | undefined;
  numLags: number | undefined;
  suggestedNumLags: number | undefined;
  onLagUpdate: (lags: number) => void;
  proxyData: BulkPickerDetailedProxyMetadata | undefined;
}

export const LayeredProxyTypeSelector = ({
  firstLayerProxyTypeValue,
  onFirstLayerProxyTypeSelect,
  investments,
  rawInvestmentRanges,
  firstLayerProxyRange,
  secondLayerProxyRange,
  withExtrapolation,
  showSecondLayerControls,
  secondLayerProxyTypeValue,
  onSecondLayerProxyTypeSelect,
  proxyConfiguration,
  onRemoveSecondLayer,
  secondLayerNumLags,
  secondLayerSuggestedNumLags,
  onSecondLayerLagUpdate,
  secondLayerFrequency,
  selectedProxyType,
  numLags,
  suggestedNumLags,
  onLagUpdate,
  proxyData,
}: LayeredProxyTypeSelectorProps) => {
  const isMulti = LayeredProxyConfigurationEnum.$is('Multi')(proxyConfiguration);

  // Calculate effective investment ranges after backfill for second layer proxy validation
  // When first layer is BACKFILL, the effective range starts earlier (at proxy start)
  const effectiveInvestmentRangesForSecondLayer = useMemo(() => {
    const isFirstLayerBackfill =
      LayeredProxyConfigurationEnum.$is('SingleLayerable')(proxyConfiguration) ||
      LayeredProxyConfigurationEnum.$is('Multi')(proxyConfiguration);

    if (!isFirstLayerBackfill || !firstLayerProxyRange) {
      return rawInvestmentRanges;
    }

    return rawInvestmentRanges.map((range) => calculateBackfilledRange(range, firstLayerProxyRange));
  }, [proxyConfiguration, rawInvestmentRanges, firstLayerProxyRange]);

  return (
    <div className="flex items-start gap-[8px]">
      <div className="flex flex-row items-end gap-[8px]">
        <div className="flex flex-row">
          <FirstLayerProxyTypeDropdown
            value={firstLayerProxyTypeValue}
            investments={investments}
            rawInvestmentRanges={rawInvestmentRanges}
            selectedProxyRange={firstLayerProxyRange}
            withExtrapolation={withExtrapolation}
            onSelect={onFirstLayerProxyTypeSelect}
            className={cn(!isMulti && '[&_.qa-dropmenu-trigger]:!rounded-r-none')}
          />
          {!isMulti && (
            <DesmoothingLag
              investments={investments}
              selectedProxyType={selectedProxyType}
              numLags={numLags}
              suggestedNumLags={suggestedNumLags}
              onLagUpdate={onLagUpdate}
              proxyData={proxyData}
              inputClassName="!h-[35px] !rounded-l-none !border-l-0 w-full"
              helpTextClassName="ml-[8px]"
            />
          )}
        </div>
        {showSecondLayerControls && (
          <div className="flex items-end gap-[8px]">
            <span
              className="flex items-center justify-center text-[14px] font-black text-venn-grey-80 h-[35px]"
              data-testid="qa-add-second-layer-symbol"
            >
              +
            </span>
            <SecondLayerProxyTypeSelector
              value={secondLayerProxyTypeValue}
              onSelect={onSecondLayerProxyTypeSelect}
              investments={investments}
              rawInvestmentRanges={effectiveInvestmentRangesForSecondLayer}
              selectedProxyRange={secondLayerProxyRange}
              withExtrapolation={withExtrapolation}
              configuration={proxyConfiguration}
              onRemove={onRemoveSecondLayer}
              numLags={secondLayerNumLags}
              suggestedNumLags={secondLayerSuggestedNumLags}
              onLagUpdate={onSecondLayerLagUpdate}
              frequency={secondLayerFrequency}
            />
          </div>
        )}
      </div>
      {isMulti &&
        secondLayerProxyTypeValue?.proxyType &&
        typeSupportsDesmooth(secondLayerProxyTypeValue.proxyType) &&
        investments.length > 1 && <DesmoothingLagHelpText />}
    </div>
  );
};
